// Shared primitives for the Screener-style entity tables used by both
// the Screener and Watchlist panels.
//
// Layout: a two-pane table. The left pane holds a frozen Name column.
// The right pane scrolls horizontally and holds the other eight columns:
// Country | Sub-segment | Revenue | Total Assets | Net Assets | Current
// Liabilities | Employees | City.
//
// Column widths, order, and visibility live in panel-local state and
// are persisted to localStorage per panel — see ./use-column-config.ts.

// Initial / fallback width for the frozen Name column.
export const FROZEN_NAME_DEFAULT_WIDTH = 200;
/** Minimum allowed width for any column when the user resizes. */
import { EMPTY_VALUE, formatMoney as fmtMoney, formatCount as fmtCount } from '../format.js';

export const COLUMN_MIN_WIDTH = 40;

// IDs of the 8 columns that live in the scrollable right pane and can
// be resized / reordered / hidden by the user.
export const RIGHT_COLUMN_IDS = [
  'countryCode',
  'subSegment',
  'revenue',
  'totalAssets',
  'netAssets',
  'currentLiabilities',
  'employees',
  'city',
] as const;
export type RightColumnId = (typeof RIGHT_COLUMN_IDS)[number];

// Per-column metadata. Labels are i18n keys resolved at render time.
// `defaultWidth` matches the column footprint we shipped before the
// resize feature; new users land on the same layout they had.
export interface ColumnDef {
  readonly labelKey: string;
  readonly defaultWidth: number;
  /** Right-align + monospace formatting (revenue, assets, employee count). */
  readonly numeric: boolean;
}

export const COLUMN_DEFS: Record<RightColumnId, ColumnDef> = {
  countryCode: {
    labelKey: 'panels.screener.columns.country',
    defaultWidth: 40,
    numeric: false,
  },
  subSegment: {
    labelKey: 'panels.screener.columns.subSegment',
    defaultWidth: 120,
    numeric: false,
  },
  revenue: { labelKey: 'panels.screener.columns.revenue', defaultWidth: 80, numeric: true },
  totalAssets: {
    labelKey: 'panels.screener.columns.totalAssets',
    defaultWidth: 90,
    numeric: true,
  },
  netAssets: { labelKey: 'panels.screener.columns.netAssets', defaultWidth: 80, numeric: true },
  currentLiabilities: {
    labelKey: 'panels.screener.columns.currentLiabilities',
    defaultWidth: 110,
    numeric: true,
  },
  employees: { labelKey: 'panels.screener.columns.employees', defaultWidth: 70, numeric: true },
  city: { labelKey: 'panels.screener.columns.city', defaultWidth: 100, numeric: false },
};

// Default visible-and-ordered state — order matches the spec, all
// columns visible.
export function defaultRightColumnState(): RightColumnState[] {
  return RIGHT_COLUMN_IDS.map((id) => ({
    id,
    width: COLUMN_DEFS[id].defaultWidth,
    visible: true,
  }));
}

export interface RightColumnState {
  id: RightColumnId;
  width: number;
  visible: boolean;
}

// Shape of the latest_financials payload inlined on /entities and
// /lists/:kind responses. Numeric fields are Postgres numerics rendered
// as strings; employeeCount is an integer. Null when the entity has no
// filed period.
export interface LatestFinancials {
  revenue: string | null;
  totalAssets: string | null;
  equity: string | null;
  creditors: string | null;
  employeeCount: number | null;
  /** Same value from the second-most-recent filed period — null when
   *  the entity has only one period on file. Used to compute y/y growth
   *  for the Screener Technical filters. */
  priorRevenue?: string | null;
  priorEmployeeCount?: number | null;
}

// Thin wrappers around the workspace-wide format primitives. The
// Watchlist + Screener panels use these to keep one set of call sites
// per "kind of number" while the actual presentation rules live in
// ../format.ts.

export function formatEur(raw: string | null | undefined): string {
  if (raw == null) return EMPTY_VALUE;
  const n = Number(raw);
  if (!Number.isFinite(n)) return EMPTY_VALUE;
  return fmtMoney(n, { currency: '€' });
}

export function formatCount(n: number | null | undefined): string {
  return fmtCount(n);
}

/** Build the CSS gridTemplateColumns string from a visible column state. */
export function gridTemplateColumns(columns: RightColumnState[]): string {
  return columns
    .filter((c) => c.visible)
    .map((c) => `${c.width}px`)
    .join(' ');
}

/** Sum of visible column widths — used as the right pane's min-width so
 *  horizontal scroll triggers when the panel is narrower than the table. */
export function visibleWidth(columns: RightColumnState[]): number {
  return columns.filter((c) => c.visible).reduce((sum, c) => sum + c.width, 0);
}
